import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'
import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/Person'
import LanguageIcon from '@mui/icons-material/Language'
import WidgetsIcon from '@mui/icons-material/Widgets'
import InfoIcon from '@mui/icons-material/Info'
import LogoutIcon from '@mui/icons-material/Logout'
import DevicesIcon from '@mui/icons-material/Devices'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto'
import { useState } from 'react'

import { logout } from '@/store/authSlice'
import { baseApi } from '@/store/api/baseApi'
import { useSignOutMutation } from '@/store/api/authApi'
import { useSelector } from 'react-redux'
import { useGetUserQuery } from '@/store/api/usersApi'
import { DRAWER_WIDTH } from './Sidebar'
import { useFlavorTheme } from '@/theme'
import UserAvatar from '@/components/users/UserAvatar'
import ActiveSessionsDialog from '@/components/ActiveSessionsDialog'
import NotificationBell from '@/components/notifications/NotificationBell'
import NotificationPreferencesDialog from '@/components/notifications/NotificationPreferencesDialog'

const LANGUAGES = [
    { key: 'en', value: 'English' },
    { key: 'es', value: 'Español' },
    { key: 'ca', value: 'Català' },
    { key: 'eu', value: 'Euskara' },
    { key: 'fr', value: 'Français' },
    { key: 'de', value: 'Deutsch' },
    { key: 'pt', value: 'Português' },
    { key: 'nl', value: 'Nederlands' },
    { key: 'pl', value: 'Polski' },
    { key: 'ar', value: 'العربية' },
    { key: 'zh', value: '中文 (简体)' }
]

// i18n usa load: 'languageOnly'; un valor guardado con región (p. ej. en-GB) se reduce al idioma base.
const resolveLocale = (lang) => {
    const base = lang?.split('-')[0]
    return LANGUAGES.some((l) => l.key === base) ? base : 'en'
}

const AppHeader = ({ onMenuToggle, showMenuButton }) => {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const userId = useSelector((s) => s.auth.userId)
    const refreshToken = useSelector((s) => s.auth.refreshToken)
    const { data: user } = useGetUserQuery(userId, { skip: !userId })
    const [signOut] = useSignOutMutation()
    const [anchorEl, setAnchorEl] = useState(null)
    const [languageAnchorEl, setLanguageAnchorEl] = useState(null)
    const [themeAnchorEl, setThemeAnchorEl] = useState(null)
    const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false)
    const [notificationPrefsOpen, setNotificationPrefsOpen] = useState(false)
    const { themeMode, setThemeMode, sidebarColors } = useFlavorTheme()
    const currentLocale = resolveLocale(i18n.language)
    const currentLanguage = LANGUAGES.find((l) => l.key === currentLocale)

    const handleSelectLanguage = (value) => {
        i18n.changeLanguage(value)
        setLanguageAnchorEl(null)
    }

    const themeIcon =
        themeMode === 'dark' ? (
            <Brightness4Icon fontSize='small' />
        ) : themeMode === 'light' ? (
            <Brightness7Icon fontSize='small' />
        ) : (
            <BrightnessAutoIcon fontSize='small' />
        )

    const handleSelectThemeMode = (mode) => {
        setThemeMode(mode)
        setThemeAnchorEl(null)
    }

    const handleLogout = () => {
        setAnchorEl(null)
        if (refreshToken) signOut(refreshToken).catch(() => {})
        dispatch(baseApi.util.resetApiState())
        dispatch(logout())
        navigate('/login', { replace: true })
    }

    return (
        <AppBar
            position='fixed'
            elevation={0}
            color='inherit'
            sx={{
                width: showMenuButton ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
                ml: showMenuButton ? 0 : `${DRAWER_WIDTH}px`,
                bgcolor: sidebarColors.bg,
                color: sidebarColors.activeText,
                borderBottom: `1px solid ${sidebarColors.dividerColor}`,
                // pt en el AppBar (sin min-height) para que crezca limpio; en el Toolbar el
                // min-height de MUI absorbería el padding cuando no hay contenido (ver spacer)
                pt: 'env(safe-area-inset-top)'
            }}>
            <Toolbar>
                {showMenuButton && (
                    <IconButton edge='start' onClick={onMenuToggle} sx={{ mr: 1 }}>
                        <MenuIcon />
                    </IconButton>
                )}

                <Typography variant='h6' noWrap sx={{ flexGrow: 1 }} />

                {/* User avatar + menu */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationBell onOpenPreferences={() => setNotificationPrefsOpen(true)} />
                    <IconButton
                        onClick={(e) => setLanguageAnchorEl(e.currentTarget)}
                        size='small'
                        aria-label={t('Language')}>
                        <LanguageIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                        onClick={(e) => setThemeAnchorEl(e.currentTarget)}
                        size='small'
                        aria-label={t('Theme')}>
                        {themeIcon}
                    </IconButton>
                    <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size='small'>
                        <UserAvatar user={user} size={32} />
                    </IconButton>
                </Box>

                <Menu
                    anchorEl={languageAnchorEl}
                    open={!!languageAnchorEl}
                    onClose={() => setLanguageAnchorEl(null)}
                    disableScrollLock
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    slotProps={{ paper: { sx: { minWidth: 180 } } }}>
                    {LANGUAGES.map(({ key, value }) => (
                        <MenuItem
                            key={key}
                            selected={key === currentLocale}
                            onClick={() => handleSelectLanguage(key)}>
                            <ListItemIcon>
                                <LanguageIcon fontSize='small' />
                            </ListItemIcon>
                            <ListItemText>{value}</ListItemText>
                        </MenuItem>
                    ))}
                </Menu>

                <Menu
                    anchorEl={themeAnchorEl}
                    open={!!themeAnchorEl}
                    onClose={() => setThemeAnchorEl(null)}
                    disableScrollLock
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    slotProps={{ paper: { sx: { minWidth: 180 } } }}>
                    <MenuItem
                        selected={themeMode === 'auto'}
                        onClick={() => handleSelectThemeMode('auto')}>
                        <ListItemIcon>
                            <BrightnessAutoIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('Auto')}</ListItemText>
                    </MenuItem>
                    <MenuItem
                        selected={themeMode === 'light'}
                        onClick={() => handleSelectThemeMode('light')}>
                        <ListItemIcon>
                            <Brightness7Icon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('Light')}</ListItemText>
                    </MenuItem>
                    <MenuItem
                        selected={themeMode === 'dark'}
                        onClick={() => handleSelectThemeMode('dark')}>
                        <ListItemIcon>
                            <Brightness4Icon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('Dark')}</ListItemText>
                    </MenuItem>
                </Menu>

                <Menu
                    anchorEl={anchorEl}
                    open={!!anchorEl}
                    onClose={() => setAnchorEl(null)}
                    disableScrollLock
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    slotProps={{ paper: { sx: { minWidth: 220 } } }}>
                    {/* Identity header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
                        <UserAvatar user={user} size={40} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant='body2' fontWeight={600} noWrap>
                                {user?.fullName}
                            </Typography>
                            <Typography variant='caption' color='textSecondary' noWrap>
                                {user?.username}
                            </Typography>
                        </Box>
                    </Box>
                    <Divider />
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null)
                            navigate('/profile')
                        }}>
                        <ListItemIcon>
                            <PersonIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('Profile')}</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null)
                            navigate('/widget-generator')
                        }}>
                        <ListItemIcon>
                            <WidgetsIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('Open widget generator')}</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null)
                            setSessionsDialogOpen(true)
                        }}>
                        <ListItemIcon>
                            <DevicesIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('Active sessions')}</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null)
                            navigate('/about')
                        }}>
                        <ListItemIcon>
                            <InfoIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('About')}</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('Logout')}</ListItemText>
                    </MenuItem>
                </Menu>

                <ActiveSessionsDialog
                    open={sessionsDialogOpen}
                    onClose={() => setSessionsDialogOpen(false)}
                />
                <NotificationPreferencesDialog
                    open={notificationPrefsOpen}
                    onClose={() => setNotificationPrefsOpen(false)}
                />
            </Toolbar>
        </AppBar>
    )
}

export default AppHeader
